import { TextInput, TextInputProps } from 'components/forms/TextInput';
import { BaseInlineEditorProps, InlineEditor } from 'components/inline_edit/InlineEditor';
import {
    useSerializedInlineEditor,
    UseSerializedInlineEditorOptions,
} from 'custom_hooks/useInlineEditor';

export type GenericTextInlineEditorProps<TValue> = BaseInlineEditorProps<TValue, string> &
    Pick<UseSerializedInlineEditorOptions<TValue, string>, 'serialize' | 'deserialize'> &
    Readonly<{
        /**
         * Optional text input config.
         */
        textInputConfig?: Partial<Omit<TextInputProps, 'value' | 'onChange'>>;
        /**
         * Optional function for formatting the input
         */
        formatInput?: (value: string | undefined) => string | undefined;
        /**
         * Optional type such as "text", "number".
         */
        type?: 'text' | 'search' | 'email' | 'password' | 'hidden' | 'number';
    }>;

/**
 * This generic text inline editor is meant to be used as a base for text
 * inline editors that convert a type to a string and back again.
 * See `TextInlineEditor` and `NumberInlineEditor`.
 */
export function GenericTextInlineEditor<TValue>({
    value,
    renderValue,
    validate,
    onConfirm,
    onClose,
    deserialize,
    serialize,
    isDisabled,
    editorClass,
    editorStyle,
    valueStyle,
    textInputConfig,
    formatInput,
    valueClass,
    type,
    ariaLabel,
}: GenericTextInlineEditorProps<TValue>) {
    const {
        valueToRender,
        inputValue,
        error,
        setInputValue,
        isEditing,
        isConfirming,
        openEditor,
        closeEditor,
        confirm,
        canConfirm,
    } = useSerializedInlineEditor<TValue, string>({
        value,
        validate,
        onConfirm,
        onClose,
        deserialize,
        serialize,
    });

    return (
        <InlineEditor
            ariaLabel={ariaLabel}
            value={renderValue ? renderValue(valueToRender) : <>{valueToRender}</>}
            editor={
                <>
                    <TextInput
                        type={type}
                        className="inline-block full-width"
                        {...textInputConfig}
                        value={inputValue}
                        invalid={error !== undefined}
                        onChange={(e) => {
                            setInputValue(
                                formatInput ? formatInput(e.target.value) : e.target.value
                            );
                        }}
                        onKeyDown={(event) => event.stopPropagation()}
                        disabled={isDisabled}
                    />
                </>
            }
            valueClass={valueClass}
            editorClass={editorClass}
            editorStyle={editorStyle}
            valueStyle={valueStyle}
            error={error}
            canClose={!isConfirming}
            canEdit={!isConfirming}
            canConfirm={canConfirm}
            isEditing={isEditing}
            isDisabled={isDisabled || isConfirming}
            isPending={isConfirming}
            onClose={closeEditor}
            onEdit={openEditor}
            onConfirm={confirm}
        />
    );
}
